import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * TABLE
 *
 * Most screens in this product are a list of things — calls, drivers, impounds,
 * invoices — so the table carries more weight than any other component here.
 *
 * Two decisions worth knowing about:
 *   • One density, not two. `density` used to be a variant, on the theory that
 *     a settings table wants room to breathe while a call log wants rows. In
 *     practice 14 of 25 tables were comfortable only because that was the
 *     default — none of them chose it — and two tables in the same feature
 *     disagreed. Padding never constrains content anyway: a cell holding a
 *     32px button is 44px tall either way. So the scanning density won.
 *   • The header is sticky by default. A table that scrolls its column labels
 *     off the top is unusable at the row counts these screens hit.
 */
export const tableRecipe = cva(
  [uiMarker, 'w-full caption-bottom border-collapse font-sans text-body-sm'],
  {
    variants: {
      /** Fixed layout stops columns jumping as data loads. */
      layout: {
        auto: 'table-auto',
        fixed: 'table-fixed',
      },
    },
    defaultVariants: { layout: 'auto' },
  }
);

export type TableRecipeProps = VariantProps<typeof tableRecipe>;

/** Scroll container. A table wider than the screen scrolls itself, not the page. */
export const tableScrollRecipe = cva(
  [uiMarker, uiMarkerLayout, 'w-full overflow-x-auto'],
  {
    variants: {
      bordered: {
        true: 'rounded-xl border border-border',
        false: '',
      },
    },
    defaultVariants: { bordered: true },
  }
);

export const tableHeaderRecipe = cva(
  [uiMarker, uiMarkerLayout, 'bg-muted/50'],
  {
    variants: {
      sticky: {
        true: 'sticky top-0 z-10 backdrop-blur',
        false: '',
      },
    },
    defaultVariants: { sticky: true },
  }
);

export const tableHeadRecipe = cva(
  [
    uiMarker,
    'border-b border-border text-left align-middle font-sans text-label text-muted-foreground',
    // 32px, matching a body row (6 + 19 + 6). A fixed height rather than the
    // cells' padding so the header band stays put whatever a label wraps to.
    'h-8 px-3',
  ],
  {
    variants: {
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
      /** Column headers that sort are interactive and must look it. */
      sortable: {
        true: 'cursor-pointer select-none transition-colors duration-fast hover:text-foreground',
        false: '',
      },
      /**
       * Matches the cells below it. A numeric column whose header stays
       * left-aligned reads as a different column from its own values.
       */
      numeric: {
        true: 'text-right',
        false: '',
      },
      /**
       * A column that only fits from a breakpoint up — the category or the
       * payment method, which a phone has no room for. Set the same value on
       * the column's TableHead and each TableCell. Written out per breakpoint
       * because Tailwind reads class names from source.
       */
      hideBelow: {
        none: '',
        sm: 'hidden sm:table-cell',
        md: 'hidden md:table-cell',
        lg: 'hidden lg:table-cell',
      },
      /**
       * The column that stays put while the rest scroll under it — the
       * row-action column at the right edge of a wide import table. Opaque,
       * with a rule on its inner edge, so the scrolled columns disappear
       * behind it rather than showing through. Set the same value on the
       * column's TableHead and each TableCell.
       */
      pinned: {
        none: '',
        end: 'sticky right-0 z-10 border-l border-border bg-background',
      },
    },
    compoundVariants: [
      // The sort control is inline-flex, so the cell's text-align does not move
      // it — it has to be pushed over itself.
      { numeric: true, sortable: true, class: '[&>button]:ml-auto' },
    ],
    defaultVariants: {
      align: 'left',
      sortable: false,
      numeric: false,
      hideBelow: 'none',
      pinned: 'none',
    },
  }
);

export const tableRowRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'border-b border-border transition-colors duration-fast last:border-0',
  ],
  {
    variants: {
      interactive: {
        true: 'cursor-pointer hover:bg-muted/60',
        false: '',
      },
      selected: {
        true: 'bg-info-subtle/60',
        false: '',
      },
    },
    defaultVariants: { interactive: false, selected: false },
  }
);

export const tableCellRecipe = cva([uiMarker, 'align-middle px-3 py-1.5'], {
  variants: {
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    /** Right-align and tabular-align money and counts so digits stack. */
    numeric: {
      true: 'text-right tabular-nums',
      false: '',
    },
    truncate: {
      true: 'max-w-0 truncate',
      false: '',
    },
    /** Matches the TableHead above it; see there. */
    hideBelow: {
      none: '',
      sm: 'hidden sm:table-cell',
      md: 'hidden md:table-cell',
      lg: 'hidden lg:table-cell',
    },
    /**
     * Matches the TableHead above it; see there. No z-index: a positioned
     * cell already paints over the static cells beside it, and giving it the
     * header's z-10 let scrolled body rows cover the sticky header.
     */
    pinned: {
      none: '',
      end: 'sticky right-0 border-l border-border bg-background',
    },
    /**
     * A cell whose value failed validation — a row of the bulk import grid
     * with a date that would not parse. Drawn as a box rather than a tint
     * alone because the cells around it are the same colour and a stain
     * with no edge does not say *which* one.
     */
    invalid: {
      true: 'border-2 border-destructive bg-destructive-subtle',
      false: '',
    },
  },
  defaultVariants: {
    align: 'left',
    numeric: false,
    truncate: false,
    hideBelow: 'none',
    pinned: 'none',
    invalid: false,
  },
});

/** The "nothing here yet" row, spanning the full width of the table. */
export const tableEmptyRecipe = cva([
  uiMarker,
  'px-4 py-12 text-center font-sans text-body text-muted-foreground',
]);
